import {
  getTransactionIntent,
  TransactionIntentStatus,
  type TransactionSubmission,
  TransactionSubmissionStatus,
} from '@/api/wallet/queries';

export class RejectedSubmissionError extends Error {
  constructor() {
    super('The transaction was rejected by the network.');
  }
}

const STATUS_POLL_ATTEMPTS = 15;
const STATUS_POLL_INTERVAL_MS = 1_000;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

export async function awaitSubmittedDigest(
  intentId: string,
  initial: TransactionSubmission,
): Promise<string> {
  let current = initial;

  for (let attempt = 0; attempt < STATUS_POLL_ATTEMPTS; attempt += 1) {
    const digest = settledDigest(current);

    if (digest) {
      return digest;
    }

    if (attempt > 0) {
      await wait(STATUS_POLL_INTERVAL_MS);
    }

    current = await readSubmission(intentId, current);
  }

  const digest = settledDigest(current);

  if (digest) {
    return digest;
  }

  throw new Error('The transaction is still processing. Please check again shortly.');
}

function settledDigest(submission: TransactionSubmission): string | null {
  if (submission.status === TransactionSubmissionStatus.FAILED) {
    throw new RejectedSubmissionError();
  }

  const submitted = submission.status === TransactionSubmissionStatus.SUBMITTED;
  return submitted && submission.digest ? submission.digest : null;
}

async function readSubmission(
  intentId: string,
  previous: TransactionSubmission,
): Promise<TransactionSubmission> {
  const intent = await getTransactionIntent(intentId);
  return {
    submissionId: intent.submissionId ?? previous.submissionId,
    status: submissionStatusOf(intent.status),
    digest: intent.digest,
    failureCode: intent.failureCode,
  };
}

function submissionStatusOf(status: TransactionIntentStatus): TransactionSubmissionStatus {
  if (status === TransactionIntentStatus.SUBMITTED) {
    return TransactionSubmissionStatus.SUBMITTED;
  }

  if (status === TransactionIntentStatus.FAILED || status === TransactionIntentStatus.EXPIRED) {
    return TransactionSubmissionStatus.FAILED;
  }

  return TransactionSubmissionStatus.SUBMITTING;
}
